import type { LessonStandard } from "@shared/models/card-stack"
import type * as filestack from "filestack-js"
import { createContext, useContext } from "react"
import type { GoogleDrivePickedFile } from "./extensions/link/link-upload"
import type { SurfaceArbiter } from "./surface-arbiter"

/**
 * Everything an editor hands to the UI inside its EditorContent (ReactNodeViews
 * and their descendants). Read this value via `useUiContext()`.
 *
 * Extensions that need access to these fields outside of their ReactNodeView
 * must instead take them as extension options.
 *
 * Every field should hold its identity for the life of a mounted editor — a change here re-renders
 * every node view.
 */
export interface UnifiedTiptapUiValue {
  // General settings

  /** Must be identity-stable while its values are unchanged — a new object re-renders every reader. */
  permissions: DerivedPermissions
  /** Whether to hide all Menu and Add buttons. */
  hideButtons?: boolean

  // Lesson settings

  /** A non-instructional lesson holds untitled notes, not lesson parts. */
  isNonInstructional?: boolean
  /**
   * This lesson as a student sees it — the version the class website carries, holding only
   * the cards marked "Show Card on Class Website". Undefined for a card stack that is on no day of
   * no course (a template, a unit): the card menu then celebrates that toggle without a link.
   */
  studentLessonUrl?: string

  // Modal hooks

  /**
   * Open the Google Classroom sidebar for the given card id. When undefined, clicking a
   * Google Classroom card does nothing.
   */
  openGoogleClassroomSidebar?: (cardId: string) => void
  /** Required for attaching files and images; without it the Add menu omits Attachment. */
  filestackClient?: filestack.Client
  /**
   * Open the standards search modal. When undefined, the Add menu omits its Standards option.
   */
  openStandardsModal?: (startingStandards: LessonStandard[]) => Promise<Array<LessonStandard>>
  /**
   * Open the host app's Google Drive picker, resolving with the picked files. When
   * undefined, the Add menu's Google Drive option is omitted.
   */
  addFromGoogleDrive?: () => Promise<GoogleDrivePickedFile[]>

  // External UI integrations

  /** The editor's contextual-surface arbiter. */
  arbiter: SurfaceArbiter
  /** Reports an open card overlay (the Add or card menu) to the arbiter. */
  setCardOverlayOpen: (isOpen: boolean) => void

  // Misc

  /** The current user's id, or null if unknown. */
  currentUserId: string | null
  commentActions: CommentActions
  onDeleteNote?: () => void
  /** Placeholders for dateAnnotationNote's title and description. */
  notePrompts?: { title: string; description: string }
}

/** Read it with {@link useUiContext}; {@link import("./UnifiedTiptapUiProvider")} supplies it. */
export const UnifiedTiptapUiContext = createContext<UnifiedTiptapUiValue | null>(null)

/** The editor UI context. Throws outside a UnifiedTiptapUiProvider. */
export function useUiContext(): UnifiedTiptapUiValue {
  const ui = useContext(UnifiedTiptapUiContext)
  if (!ui) throw new Error("useUiContext must be used inside a UnifiedTiptapUiProvider")
  return ui
}

// ---------
// Sub-fields
// ---------

/**
 * Permissions for the current editor.
 *
 * These are not necessarily the CardStack's `meta.permissions`: the current component may further restrict them
 * (e.g., in LessonSimpleReadonly).
 */
export interface DerivedPermissions {
  /** Equal to `editor.isEditable`. */
  readonly canEdit: boolean
  readonly canComment: boolean
}

/**
 * The lesson's comment callbacks. Currently just for card comments.
 */
export interface CommentActions {
  /** Soft-deletes a comment (sets its `deletedAt`), dispatching DELETE_COMMENT. */
  deleteComment: (commentId: string) => void
  /**
   * Adds a comment to the card with id `cardId` (the comment's adverbialObject), with the
   * given HTML text.
   */
  addComment: (cardId: string, cardTitle: string | null, commentText: string) => void
}

/** CommentActions whose callbacks do nothing. */
export const EMPTY_COMMENT_ACTIONS: CommentActions = {
  deleteComment: () => {},
  addComment: () => {},
}
